import React, {useEffect, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary, Alert, AlertTitle,
    Box,
    Button,
    Card,
    Grid, IconButton, Snackbar,
    Typography
} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {Close, ExclamationMarkWithCircle, Upload} from "~/ui/icons";
import {FileCard} from "~/apps/core/ui/FileCard";
import {UploadFilesButton} from "~/apps/core/ui/UploadFilesButton";
import {humanizeFileType, TFileType} from "~/apps/packages/passes/utils";
import {PortTheme} from "~/ui/theme";
import {ConfirmActionDialog} from "~/components/ConfirmActionDialog";
import {PopupMessage} from "~/components/PopupMessage";
import {ErrorMessage} from "~/components/ErrorHandling/AlertErrorHandlerProvider";
import {Values} from "~/apps/packages/passes/components/PassForm/PassForm";
import {useFormContext} from "react-hook-form";
import {useHistory, useLocation} from "react-router-dom";

const useStyles = makeStyles<PortTheme>((theme) => ({
    accordionSummaryContent: {
        display: "flex",
        alignItems: "center",
    },
    accordionSummaryText: {
        marginLeft: 8,
    },
    accordionDetailsUl: {
        padding: 0,
        listStyle: "none",
    },
    learnMoreButton: {
        backgroundColor: 'rgba(63, 121, 181, 0.12)',
        color: '#194F87',
    }
}))

const MAX_FILE_SIZE = 3 * 1024 * 1024;
const MIN_FILE_SIZE = 50 * 1024;
const MAX_FILES_COUNT = 30;
const ACCEPT_MIME_TYPES = "image/png, image/jpeg, image/bmp, image/jpg";

type UploadFormCardProps = {
    loading: boolean,
}

const fileSizeNotification: ErrorMessage = {
    title: 'Превышен размер файла',
    message: 'Допустимый размер от 50 Кб до 3 Мб.'
}

const duplicateFileNotification: ErrorMessage = {
    title: 'Повтор файла',
    message: 'Этот файл уже был добавлен ранее.'
}

const fileTypeErrorNotification: ErrorMessage = {
    title: 'Неверный формат файла',
    message: 'Допустимые форматы: png, jpg, jpeg, bmp.'
}

export const UploadFormCard = (props: UploadFormCardProps) => {
    const { loading } = props;
    const { control, setValue, getValues, watch, formState } = useFormContext<Values>();
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState<{open: boolean, file: File}>({open: false, file: null});
    const [isAlertVisible, setIsAlertVisible] = React.useState({open: false, notification: null});
    const [isUploadButtonLoading, setUploadButtonLoading] = useState(false);
    const locationSearch = useRef(null);

    const history = useHistory();
    const location = useLocation();

    const styles = useStyles();

    const files: File[] = watch("documents", []);

    const handleOpenConfirmDialog = (file: File) => {
        locationSearch.current = history.location.search;
        setIsConfirmDialogOpen({open: true, file});
    }

    const handleCloseConfirmDialog = () => {
        setIsConfirmDialogOpen({open: false, file: null});

        //TODO: Fix this hack
        if (locationSearch.current) {
            history.replace({
                ...location,
                search: locationSearch.current
            });
        }
    }

    const handleAddFiles = (files: File[]) => {
        const documents = getValues("documents") || [];
        setUploadButtonLoading(true);
        files.forEach(file => {
            if (!file.type || !ACCEPT_MIME_TYPES.includes(file.type)) {
                setIsAlertVisible({open: true, notification: fileTypeErrorNotification});
                return;
            }
            if (file.size <= MAX_FILE_SIZE && file.size >= MIN_FILE_SIZE) {
                if (documents.find(f => f.name === file.name)) {
                    setIsAlertVisible({open: true, notification: duplicateFileNotification});
                    return;
                } else {
                    documents.push(file);
                }
            }
            else {
                setIsAlertVisible({open: true, notification: fileSizeNotification});
            }
        });
        setUploadButtonLoading(false)
        setValue("documents", documents);
    }
    const handleRemoveFile = (file: File) => {
        const documents = getValues("documents") || [];
        setValue("documents", documents.filter(f => f !== file));
        setIsConfirmDialogOpen({open: false, file: null});

        //TODO: Fix this hack
        if (locationSearch.current) {
            history.replace({
                ...location,
                search: locationSearch.current
            });
        }
    }

    useEffect(() => {
        setValue("documents", []);
    }, [])



    return (
        <>
            <ConfirmActionDialog
                open={isConfirmDialogOpen.open}
                dialogId="removeFile"
                confirmButtonText="Удалить"
                onClose={handleCloseConfirmDialog}
                onConfirm={() => handleRemoveFile(isConfirmDialogOpen.file)}
            >
                <Typography
                    width="100%"
                    align="center"
                    variant="screenText"
                >
                    Вы хотите удалить файл?
                </Typography>
            </ConfirmActionDialog>
            <Card>
                <Accordion>
                    <AccordionSummary>
                        <Box className={styles.accordionSummaryContent}>
                            <ExclamationMarkWithCircle />
                            <Typography className={styles.accordionSummaryText} variant="cardTitle2" color="mainPrimary">Загрузка файла</Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="cardText" color="mainPrimary">
                            Согласно п. 34 приказа Министерства транспорта РФ от 31 марта 2022 г. N 107 для получения пропуска вам необходимо приложить к заявке:
                            <ul className={styles.accordionDetailsUl}>
                                <li>1. Фото паспорта для физического лица</li>
                                <li>2. СОР для автомобиля</li>
                                <li>3. Договор для выполнения подрядных работ</li>
                            </ul>
                            <Button
                                component="a"
                                variant="contained"
                                href="https://base.garant.ru/404466544/"
                                fullWidth
                                size="small"
                                color={'additionalPrimary'}
                                //color="additionalPrimary"
                                className={styles.learnMoreButton}
                                target="_blank"
                            >
                                Узнать больше
                            </Button>
                        </Typography>
                    </AccordionDetails>
                </Accordion>
            </Card>
            {
                files.map((file) => (
                    <Box marginTop="16px" key={file.name + file.size}>
                        <FileCard
                            type="upload"
                            name={file.name}
                            format={humanizeFileType(file.type as TFileType).toUpperCase()}
                            size={file.size}
                            onRemove={() => handleOpenConfirmDialog(file)}
                        />
                    </Box>
                ))
            }
            <Box marginTop="16px">
                <UploadFilesButton
                    onChange={handleAddFiles}
                    accept={ACCEPT_MIME_TYPES}
                    name="documents"
                    control={control}
                    disabled={files.length >= MAX_FILES_COUNT || loading || isUploadButtonLoading}
                    loading={isUploadButtonLoading || loading}
                />
            </Box>
            <PopupMessage
                onClose={() => setIsAlertVisible({open: false, notification: null})}
                open={isAlertVisible.open}
                notification={isAlertVisible.notification}
                autoHideDuration={5000}
            />
        </>
    )
}
